/**
 * V3 — Caisse Multi-Entités. Trésorerie consolidée par entité.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { api } from './api';
import { Download, Search, ChevronLeft, ChevronRight, TrendingUp, TrendingDown, Wallet, Plus, Pencil, Trash2, X, AlertTriangle, CheckCircle2 } from 'lucide-react';

const baseURL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Math.round(Number(n || 0)).toLocaleString('fr-FR');

const ENTITY_COLORS = {
  CIS: '#22c55e', ROPS: '#f59e0b', NETBTP: '#ef4444',
  NKEYI: '#8b5cf6', R2S: '#fb923c', EXCUISINE: '#6366f1',
};

const ENTITIES = ['ROPS', 'NETBTP', 'CIS', 'NKEYI', 'R2S', 'EXCUISINE'];
const MODES_PAIEMENT = ['Cash', 'Mobile Money', 'Banque', 'Chèque'];
const CAT_ENTREE = ['Vente', 'Encaissement client', 'Apport associé', 'Remboursement', 'Autres entrées'];
const CAT_SORTIE = ['Achat', 'Salaire', 'Charge', 'Carburant', 'Maintenance', 'Loyer', 'Charges sociales', 'Impôts', 'Banque', 'Autres sorties'];

const EMPTY_CAI_FORM = {
  entity: 'ROPS',
  date: new Date().toISOString().slice(0, 10),
  type: 'Entrée',
  montant: '',
  beneficiaire: '',
  categorie: '',
  mode_paiement: 'Cash',
  reference: '',
  notes: '',
};

export default function V3Caisse() {
  const [data, setData] = useState({ items: [], total: 0, total_pages: 0, kpis: { entrees: 0, sorties: 0, solde: 0 }, entities_available: [] });
  const [entity, setEntity] = useState('ALL');
  const [opType, setOpType] = useState('ALL');
  const [modePaiement, setModePaiement] = useState('ALL');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // CRUD state
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_CAI_FORM);
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);  // { id, entity, beneficiaire }
  const [toast, setToast] = useState(null);

  const showToast = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3500);
  };

  const openCreate = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_CAI_FORM, entity: entity === 'ALL' ? 'ROPS' : entity });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (op) => {
    setEditingId(op.id);
    setFormData({
      entity: op.entity || 'ROPS',
      date: op.date || new Date().toISOString().slice(0, 10),
      type: op.type || 'Entrée',
      montant: op.montant ? String(op.montant) : '',
      beneficiaire: op.beneficiaire || '',
      categorie: op.categorie || '',
      mode_paiement: op.mode_paiement || 'Cash',
      reference: op.reference || '',
      notes: op.notes || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormError(null);
  };

  const submitForm = async (e) => {
    e?.preventDefault?.();
    if (!formData.date) { setFormError('Date obligatoire.'); return; }
    const montantNum = Number(formData.montant);
    if (!montantNum || montantNum <= 0) { setFormError('Montant doit être > 0.'); return; }
    if (!formData.type) { setFormError('Type obligatoire (Entrée ou Sortie).'); return; }
    setBusy(true); setFormError(null);
    try {
      const payload = {
        entity: formData.entity,
        date: formData.date,
        type: formData.type,
        montant: montantNum,
        beneficiaire: formData.beneficiaire.trim() || null,
        categorie: formData.categorie.trim() || null,
        mode_paiement: formData.mode_paiement || 'Cash',
        reference: formData.reference.trim() || null,
        notes: formData.notes.trim() || null,
      };
      if (editingId) {
        await api.put(`/api/v3/caisse/${editingId}`, payload);
        showToast(`Opération mise à jour`);
      } else {
        await api.post('/api/v3/caisse', payload);
        showToast(`Opération créée (${payload.type} · ${fmt(payload.montant)} FCFA)`);
      }
      closeForm();
      fetchData();
    } catch (e) {
      setFormError(e?.response?.data?.detail || e.message || 'Erreur réseau');
    } finally {
      setBusy(false);
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    try {
      await api.delete(`/api/v3/caisse/${confirmDelete.id}?entity=${encodeURIComponent(confirmDelete.entity)}`);
      showToast(`Opération supprimée`);
      setConfirmDelete(null);
      fetchData();
    } catch (e) {
      showToast(e?.response?.data?.detail || e.message || 'Erreur', 'error');
    } finally {
      setBusy(false);
    }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { entity, op_type: opType, mode_paiement: modePaiement, page, page_size: 40 };
      if (from) params.from_date = from;
      if (to) params.to_date = to;
      if (search) params.search = search;
      const { data: d } = await api.get('/api/v3/caisse', { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [entity, opType, modePaiement, from, to, search, page]);

  useEffect(() => { Promise.resolve().then(fetchData); }, [fetchData]);

  const exportUrl = () => {
    const p = new URLSearchParams();
    p.set('entity', entity);
    if (from) p.set('from_date', from);
    if (to) p.set('to_date', to);
    return `${baseURL}/api/v3/caisse/export.xlsx?${p}`;
  };

  return (
    <div className="v3-page" data-testid="v3-caisse">
      <div className="v3-page-head">
        <h1>Caisse multi-entités</h1>
        <p>{data.total} opération{data.total>1?'s':''} · {entity === 'ALL' ? 'toutes entités' : entity}</p>
      </div>

      <section className="v3-kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <KpiCard icon={<TrendingUp/>} label="Entrées" value={fmt(data.kpis.entrees) + ' FCFA'} accent="emerald" />
        <KpiCard icon={<TrendingDown/>} label="Sorties" value={fmt(data.kpis.sorties) + ' FCFA'} accent="rose" />
        <KpiCard icon={<Wallet/>} label="Solde net" value={fmt(data.kpis.solde) + ' FCFA'} accent={data.kpis.solde >= 0 ? 'emerald' : 'rose'} />
      </section>

      <section className="v3-filters">
        <div className="v3-filter-grp">
          <label>Entité</label>
          <select value={entity} onChange={(e) => { setPage(1); setEntity(e.target.value); }} data-testid="filter-entity">
            <option value="ALL">Toutes</option>
            {data.entities_available.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Type</label>
          <select value={opType} onChange={(e) => { setPage(1); setOpType(e.target.value); }} data-testid="filter-type">
            <option value="ALL">Tous</option>
            <option value="Entree">Entrées</option>
            <option value="Sortie">Sorties</option>
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Mode paiement</label>
          <select value={modePaiement} onChange={(e) => { setPage(1); setModePaiement(e.target.value); }} data-testid="filter-mode-paiement">
            <option value="ALL">Tous</option>
            {MODES_PAIEMENT.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
        <div className="v3-filter-grp">
          <label>Du</label>
          <input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} />
        </div>
        <div className="v3-filter-grp">
          <label>Au</label>
          <input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} />
        </div>
        <div className="v3-filter-grp v3-filter-search">
          <label>Recherche</label>
          <div className="v3-search-input">
            <Search size={14}/>
            <input type="text" placeholder="Bénéficiaire, notes, réf…" value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }} />
          </div>
        </div>
        <button type="button" onClick={openCreate} className="v3-btn v3-btn-primary" data-testid="btn-new-caisse">
          <Plus size={16}/>Nouvelle opération
        </button>
        <a href={exportUrl()} className="v3-btn v3-btn-secondary" data-testid="btn-export-caisse">
          <Download size={16}/>Excel
        </a>
      </section>

      <section className="v3-card v3-card-table">
        <div className="v3-table-wrap">
          <table className="v3-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Entité</th>
                <th>N°</th>
                <th>Type</th>
                <th>Catégorie</th>
                <th>Bénéficiaire</th>
                <th>Mode</th>
                <th className="num">Montant</th>
                <th>Notes</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={`${it.id}-${it.numero}`} className="v3-row" data-testid={`caisse-row-${it.id}`}>
                  <td className="mono">{it.date}</td>
                  <td><span className="v3-entity-pill" style={{ background: (ENTITY_COLORS[it.entity] || '#94a3b8') + '20', color: ENTITY_COLORS[it.entity] || '#94a3b8' }}>{it.entity}</span></td>
                  <td className="mono">{it.numero}</td>
                  <td><span className={`v3-pill ${(it.type || '').toLowerCase().includes('entr') ? 'v3-pill-in' : 'v3-pill-out'}`}>{it.type}</span></td>
                  <td>{it.categorie || '—'}</td>
                  <td>{it.beneficiaire || '—'}</td>
                  <td>{it.mode_paiement || '—'}</td>
                  <td className={`num strong ${(it.type || '').toLowerCase() === 'sortie' ? 'negative' : ''}`}>{(it.type || '').toLowerCase() === 'sortie' ? '-' : '+'}{fmt(it.montant)}</td>
                  <td className="cell-notes">{it.notes || '—'}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button
                      onClick={() => openEdit(it)}
                      title="Modifier"
                      aria-label="Modifier"
                      data-testid={`edit-caisse-${it.id}`}
                      className="v3-icon-btn"
                      style={{ marginRight: 4 }}
                    ><Pencil size={13} /></button>
                    <button
                      onClick={() => setConfirmDelete({ id: it.id, entity: it.entity, beneficiaire: it.beneficiaire, date: it.date, montant: it.montant })}
                      title="Supprimer"
                      aria-label="Supprimer"
                      data-testid={`delete-caisse-${it.id}`}
                      className="v3-icon-btn v3-icon-btn-danger"
                    ><Trash2 size={13} /></button>
                  </td>
                </tr>
              ))}
              {!loading && data.items.length === 0 && <tr><td colSpan={10} className="v3-empty">Aucune opération.</td></tr>}
              {loading && <tr><td colSpan={10} className="v3-empty">Chargement…</td></tr>}
            </tbody>
          </table>
        </div>
        {data.total_pages > 1 && (
          <div className="v3-pagination">
            <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page <= 1}><ChevronLeft size={16}/>Préc.</button>
            <span>Page {page} / {data.total_pages}</span>
            <button onClick={() => setPage(p => Math.min(data.total_pages, p+1))} disabled={page >= data.total_pages}>Suiv.<ChevronRight size={16}/></button>
          </div>
        )}
      </section>

      {/* Modal de création/édition */}
      {showForm && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeForm(); }}>
          <form className="v3-modal" onSubmit={submitForm} data-testid="caisse-form">
            <div className="v3-modal-head">
              <h2>{editingId ? 'Modifier une opération' : 'Nouvelle opération'}</h2>
              <button type="button" onClick={closeForm} aria-label="Fermer" className="v3-icon-btn"><X size={16} /></button>
            </div>
            <div className="v3-modal-body">
              <div className="v3-form-row">
                <CaiFormField label="Entité *">
                  <select
                    required
                    value={formData.entity}
                    onChange={(e) => setFormData({ ...formData, entity: e.target.value })}
                    disabled={!!editingId}
                    data-testid="caisse-form-entity"
                  >
                    {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
                  </select>
                </CaiFormField>
                <CaiFormField label="Date *">
                  <input
                    type="date" required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="caisse-form-date"
                  />
                </CaiFormField>
              </div>
              <div className="v3-form-row">
                <CaiFormField label="Type *">
                  <select
                    required
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value, categorie: '' })}
                    data-testid="caisse-form-type"
                  >
                    <option value="Entrée">Entrée</option>
                    <option value="Sortie">Sortie</option>
                  </select>
                </CaiFormField>
                <CaiFormField label="Montant (FCFA) *">
                  <input
                    type="number" min="0" step="1" required
                    value={formData.montant}
                    onChange={(e) => setFormData({ ...formData, montant: e.target.value })}
                    placeholder="0"
                    data-testid="caisse-form-montant"
                  />
                </CaiFormField>
              </div>
              <div className="v3-form-row">
                <CaiFormField label="Catégorie">
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    data-testid="caisse-form-categorie"
                  >
                    <option value="">— Aucune —</option>
                    {(formData.type === 'Entrée' ? CAT_ENTREE : CAT_SORTIE).map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </CaiFormField>
                <CaiFormField label="Mode de paiement">
                  <select
                    value={formData.mode_paiement}
                    onChange={(e) => setFormData({ ...formData, mode_paiement: e.target.value })}
                    data-testid="caisse-form-mode"
                  >
                    {MODES_PAIEMENT.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                </CaiFormField>
              </div>
              <CaiFormField label="Bénéficiaire">
                <input
                  type="text"
                  value={formData.beneficiaire}
                  onChange={(e) => setFormData({ ...formData, beneficiaire: e.target.value })}
                  placeholder="Nom du bénéficiaire ou du payeur"
                  data-testid="caisse-form-beneficiaire"
                />
              </CaiFormField>
              <CaiFormField label="Référence">
                <input
                  type="text"
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="N° facture, reçu, virement…"
                  data-testid="caisse-form-reference"
                />
              </CaiFormField>
              <CaiFormField label="Notes">
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes facultatives"
                  rows={2}
                  data-testid="caisse-form-notes"
                />
              </CaiFormField>
              {formError && (
                <div className="v3-form-error">
                  <AlertTriangle size={14} /> {formError}
                </div>
              )}
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={closeForm} className="v3-btn v3-btn-secondary" data-testid="caisse-form-cancel">Annuler</button>
              <button type="submit" disabled={busy} className="v3-btn v3-btn-primary" data-testid="caisse-form-submit">
                {busy ? 'Enregistrement…' : (editingId ? 'Mettre à jour' : 'Créer')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Confirmation suppression */}
      {confirmDelete && (
        <div className="v3-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setConfirmDelete(null); }}>
          <div className="v3-modal v3-modal-confirm">
            <div className="v3-modal-body" style={{ textAlign: 'center' }}>
              <AlertTriangle size={36} style={{ color: '#ef4444', margin: '0 auto 12px' }} />
              <h2 style={{ marginBottom: 6 }}>Confirmer la suppression</h2>
              <p style={{ color: '#94a3b8', fontSize: 13 }}>
                Supprimer l&apos;opération du <b>{confirmDelete.date || '?'}</b>
                {confirmDelete.beneficiaire ? <> · <b>{confirmDelete.beneficiaire}</b></> : null}
                {confirmDelete.montant ? <> · <b>{fmt(confirmDelete.montant)} FCFA</b></> : null}
                {' '}({confirmDelete.entity}) ? Cette action est synchronisée avec l&apos;app legacy.
              </p>
            </div>
            <div className="v3-modal-foot">
              <button type="button" onClick={() => setConfirmDelete(null)} className="v3-btn v3-btn-secondary">Annuler</button>
              <button type="button" onClick={doDelete} disabled={busy} className="v3-btn v3-btn-danger" data-testid="confirm-delete-caisse-btn">
                {busy ? 'Suppression…' : 'Oui, supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className={`v3-toast v3-toast-${toast.type}`} data-testid="caisse-toast">
          {toast.type === 'success' ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span>{toast.text}</span>
        </div>
      )}
    </div>
  );
}

function CaiFormField({ label, children }) {
  return (
    <div className="v3-form-field">
      <label className="v3-form-field-label">{label}</label>
      {children}
    </div>
  );
}

function KpiCard({ icon, label, value, accent }) {
  return (
    <div className={`v3-kpi v3-kpi-${accent}`}>
      <div className="v3-kpi-icon">{icon}</div>
      <div className="v3-kpi-body">
        <div className="v3-kpi-label">{label}</div>
        <div className="v3-kpi-value">{value}</div>
      </div>
    </div>
  );
}
